import type { Discipline } from "@/domains/pm/types";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { getDisciplineCode, getDisciplineName } from "@/domains/pm/utils/disciplineName";

interface DisciplineBadgeProps {
  discipline: Discipline;
  size?: "sm" | "md" | "lg";
  className?: string;
  /** `solid` (default) — white-on-gray. `outline` — thin-bordered pill on
   *  white, used by the issue detail panel's TAGS group so the tags carry
   *  far less visual weight (WHA-3810). */
  variant?: "solid" | "outline";
  /** Further disciplines folded into this one chip. Those with a different
   *  code count towards a `+N`; those sharing this chip's code (KT and VN both
   *  read KVJ) add nothing to it. The tooltip names them all, so nothing is
   *  actually hidden. */
  additional?: Discipline[];
  /** Spell the discipline's localised name out instead of its code. The pill
   *  then needs no tooltip, and never wraps — `FittedDisciplineBadges` only
   *  asks for this once it has measured that the names fit on one line. */
  showName?: boolean;
}

const SIZE_CLASSES: Record<NonNullable<DisciplineBadgeProps["size"]>, string> = {
  sm: "px-1.5 text-[10px]",
  md: "px-2 py-0.5 text-[11px]",
  lg: "px-2 py-0.5 text-[14px]",
};

// WHA-3449: all discipline badges use one uniform gray — they differ only by
// their letters, not the DB-driven per-discipline color coding.
const VARIANT_STYLES: Record<
  NonNullable<DisciplineBadgeProps["variant"]>,
  { className: string; backgroundColor: string; color: string }
> = {
  solid: { className: "font-semibold", backgroundColor: "#808080", color: "#ffffff" },
  outline: {
    className: "rounded-full border border-border font-medium",
    backgroundColor: "#ffffff",
    color: "#262626",
  },
};

export function DisciplineBadge({
  discipline,
  size = "md",
  className,
  variant = "solid",
  additional,
  showName = false,
}: DisciplineBadgeProps) {
  const { language } = useLanguage();
  const { className: variantClass, ...variantStyle } = VARIANT_STYLES[variant];
  const pillClass = cn(
    "inline-flex cursor-default items-center rounded",
    SIZE_CLASSES[size],
    variantClass,
    className
  );

  if (showName) {
    return (
      <span className={cn(pillClass, "whitespace-nowrap")} style={variantStyle}>
        {getDisciplineName(discipline, language)}
      </span>
    );
  }

  const code = getDisciplineCode(discipline, language);
  const otherCodes = new Set((additional ?? []).map((d) => getDisciplineCode(d, language)));
  otherCodes.delete(code);
  const extra = otherCodes.size;
  const label = `${code}${extra > 0 ? `+${extra}` : ""}`;
  // design_disciplines.name is the Estonian name; name_en the English one.
  // Fall back to the Estonian name when the English column is empty.
  const nameOf = (d: Discipline) => (language === "en" ? d.name_en || d.name : d.name);
  const displayName = [discipline, ...(additional ?? [])].map(nameOf).join(", ");

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={pillClass} style={variantStyle}>
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent>{displayName}</TooltipContent>
    </Tooltip>
  );
}
